import React from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import { TextField, MenuItem, InputAdornment } from "@mui/material"
import { SearchOutlined, SchoolOutlined } from "@mui/icons-material"
import { Loader, ToastMessage } from "../../components/index"
import { useGetCourseCatalogQuery } from "../../Redux/actions/lecture.action"
import { getCurrentStudent } from "../session"
import { learnPaths } from "../constants"
import CourseCard from "../components/CourseCard"
import CheckoutDialog from "../components/CheckoutDialog"
import EmptyState from "../components/EmptyState"
import usePurchaseFlow from "../usePurchaseFlow"

/**
 * Portal home: the full catalogue of courses published by the school, which
 * any signed-in student can browse and buy -- not just the ones for their own
 * class.
 */
export default function LearnHomePage() {
    const navigate = useNavigate()
    const student = getCurrentStudent()

    const { data, isLoading, isError, error, refetch } =
        useGetCourseCatalogQuery()

    const [search, setSearch] = React.useState("")
    const [classFilter, setClassFilter] = React.useState("all")

    const purchase = usePurchaseFlow({
        onPurchased: (course) => navigate(learnPaths.course(course.course_id))
    })

    const courses = React.useMemo(() => _.get(data, "list", []), [data])

    /* Courses are platform-wide; their class is a free-text label. */
    const classOptions = React.useMemo(
        () =>
            _.uniq(
                courses.map((course) => course.class_name).filter(Boolean)
            ).sort(),
        [courses]
    )

    const visibleCourses = React.useMemo(() => {
        const term = search.trim().toLowerCase()
        return courses.filter((course) => {
            const matchesClass =
                classFilter === "all" || course.class_name === classFilter
            const matchesTerm =
                !term ||
                `${course.title} ${course.subject_name} ${course.instructor || ""}`
                    .toLowerCase()
                    .includes(term)
            return matchesClass && matchesTerm
        })
    }, [courses, search, classFilter])

    const ownedCount = courses.filter((course) => course.is_enrolled).length

    return (
        <div className="space-y-6">

            {/* Hero */}
            <section className="rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-5 text-white shadow-lg sm:p-8">
                <p className="text-xs font-semibold tracking-widest text-white/70">
                    COURSE CATALOGUE
                </p>
                <h1 className="mt-2 text-xl font-bold sm:text-3xl">
                    Hello{student?.firstname ? `, ${student.firstname}` : ""} 👋
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-white/80">
                    Browse every course published by your school. Enrol once and
                    watch the lectures whenever you like.
                </p>

                <div className="mt-5 flex flex-wrap gap-3">
                    <div className="rounded-xl bg-white/15 px-4 py-2">
                        <span className="block text-lg font-bold">
                            {courses.length}
                        </span>
                        <span className="text-xs text-white/70">
                            Courses available
                        </span>
                    </div>
                    <div className="rounded-xl bg-white/15 px-4 py-2">
                        <span className="block text-lg font-bold">
                            {ownedCount}
                        </span>
                        <span className="text-xs text-white/70">
                            Already yours
                        </span>
                    </div>
                </div>
            </section>

            {/* Filters */}
            {courses.length > 0 && (
                <section className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto] sm:gap-4">
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search courses, subjects or teachers"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Class"
                        value={classFilter}
                        onChange={(event) => setClassFilter(event.target.value)}
                        sx={{ minWidth: { sm: 180 } }}
                    >
                        <MenuItem value="all">All classes</MenuItem>
                        {classOptions.map((item) => (
                            <MenuItem key={item} value={item}>
                                {item}
                            </MenuItem>
                        ))}
                    </TextField>
                </section>
            )}

            {isError && (
                <EmptyState
                    icon={<SchoolOutlined />}
                    title="Couldn't load the catalogue"
                    description={
                        _.get(error, "data.detail", null) ||
                        "Please check your connection and try again."
                    }
                    actionLabel="Retry"
                    onAction={refetch}
                />
            )}

            {!isLoading && !isError && courses.length === 0 && (
                <EmptyState
                    icon={<SchoolOutlined />}
                    title="No courses published yet"
                    description="Your school hasn't published any courses so far. Please check back soon."
                />
            )}

            {courses.length > 0 && visibleCourses.length === 0 && (
                <EmptyState
                    icon={<SearchOutlined />}
                    title="No courses match that search"
                    description="Try a different subject, teacher or class."
                    actionLabel="Clear filters"
                    onAction={() => {
                        setSearch("")
                        setClassFilter("all")
                    }}
                />
            )}

            {visibleCourses.length > 0 && (
                <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {visibleCourses.map((course) => (
                        <CourseCard
                            key={course.course_id}
                            course={course}
                            onOpen={() =>
                                navigate(learnPaths.course(course.course_id))
                            }
                            onBuy={purchase.startPurchase}
                        />
                    ))}
                </section>
            )}

            <CheckoutDialog
                open={Boolean(purchase.selectedCourse)}
                course={purchase.selectedCourse}
                saving={purchase.isPurchasing}
                onClose={purchase.cancelPurchase}
                onConfirm={purchase.confirmPurchase}
            />

            {(isLoading || purchase.isPurchasing) && (
                <Loader show={true} message="Loading courses..." />
            )}

            {purchase.notification.show && (
                <ToastMessage
                    {...purchase.notification}
                    onClose={purchase.clearNotification}
                />
            )}
        </div>
    )
}
